import { Radar } from 'react-chartjs-2'

import {
  Chart as ChartJS,
  type ChartOptions,
  Filler,
  Legend,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
  type TooltipItem
} from 'chart.js'

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

const SERIES_COLORS = [
  { line: '#7D6BD8', fill: 'rgba(125, 107, 216, 0.18)' },
  { line: '#6ECBFA', fill: 'rgba(110, 203, 250, 0.18)' }
]
/** 値が無い軸に描く点。0（低い）ではなく「測れていない」ことを表す */
const MISSING_VALUE = 0

interface Series {
  title: string
  /** 0-100。null は「その軸の属性が無い」。0 点として描かない */
  values: (number | null)[]
}

interface Props {
  labels: string[]
  series: Series[]
  /** 目盛の上限。既定は 100（サイト内百分位のスケール） */
  max?: number
  height?: number
}

/**
 * 10軸レーダー。値が無い軸は 0 ではなく「—」として扱う。
 *
 * 0 と「測れていない」を同じ形で描くと、データが無いだけの軸が
 * 「その顧客は弱い」と読まれてしまう。欠測は点を描かず、
 * ツールチップで理由が分かるようにしている。
 */
const RadarChart = ({ labels, series, max = 100, height = 320 }: Props) => {
  const options: ChartOptions<'radar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        min: 0,
        max,
        beginAtZero: true,
        ticks: { stepSize: max / 4, color: '#8A94A6', backdropColor: 'transparent',
                 font: { size: 10 } },
        grid: { color: '#E4E8EE' },
        angleLines: { color: '#E4E8EE' },
        pointLabels: { color: '#34414F', font: { size: 12 } }
      }
    },
    plugins: {
      legend: {
        display: series.length > 1,
        position: 'top',
        align: 'end',
        labels: { color: '#34414F', font: { size: 12 }, boxWidth: 12, boxHeight: 12 }
      },
      tooltip: {
        backgroundColor: '#ffffff',
        titleColor: '#34414F',
        titleFont: { size: 12, weight: 'bold' },
        bodyFont: { size: 13 },
        padding: 10,
        cornerRadius: 8,
        borderColor: '#E4E8EE',
        borderWidth: 1,
        callbacks: {
          label: (context: TooltipItem<'radar'>) => {
            const original = series[context.datasetIndex]?.values[context.dataIndex]
            if (original === null || original === undefined) return ' 測れていない軸'
            return ` ${context.dataset.label}  ${original}`
          }
        }
      }
    }
  }

  return (
    <div style={{ height }}>
      <Radar
        data={{
          labels,
          datasets: series.map((s, i) => ({
            label: s.title,
            data: s.values.map((v) => (v === null ? MISSING_VALUE : v)),
            borderColor: SERIES_COLORS[i % SERIES_COLORS.length].line,
            backgroundColor: SERIES_COLORS[i % SERIES_COLORS.length].fill,
            borderWidth: 2,
            // 欠測は点を描かない。0 点として打つと「低い」と誤読される
            pointRadius: s.values.map((v) => (v === null ? 0 : 3)),
            pointBackgroundColor: SERIES_COLORS[i % SERIES_COLORS.length].line
          }))
        }}
        options={options}
      />
    </div>
  )
}

export default RadarChart
